import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatMinorUsd, type PublicInvoice } from '@fois-utsavvoice/shared';
import { ApiError } from '../api/client.js';
import { getPublicInvoice, getPublicInvoicePdfUrl } from '../api/public.js';
import { PageShell } from '../components/PageShell.js';

const UNAVAILABLE_COPY =
  'This invoice link is no longer available. Please contact FESTIVALSOFINDIASTORE if you need a copy of your invoice.';

export function PublicInvoicePage() {
  const { accessToken } = useParams<{ accessToken: string }>();
  const [invoice, setInvoice] = useState<PublicInvoice | undefined>();
  const [pageState, setPageState] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    const referrer = document.createElement('meta');
    referrer.name = 'referrer';
    referrer.content = 'no-referrer';
    document.head.append(robots, referrer);
    document.documentElement.setAttribute('data-invoice-share', 'true');
    return () => {
      robots.remove();
      referrer.remove();
      document.documentElement.removeAttribute('data-invoice-share');
    };
  }, []);

  useEffect(() => {
    if (!accessToken) {
      setPageState('unavailable');
      return;
    }
    getPublicInvoice(accessToken)
      .then((data) => {
        setInvoice(data);
        setPageState('ready');
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError && (caught.status === 410 || caught.status === 404)) {
          setPageState('unavailable');
          return;
        }
        setPageState('unavailable');
      });
  }, [accessToken]);

  async function onDownload(): Promise<void> {
    if (!accessToken) {
      return;
    }
    setError(undefined);
    setDownloading(true);
    try {
      const signed = await getPublicInvoicePdfUrl(accessToken);
      window.open(signed.url, '_blank', 'noopener,noreferrer');
    } catch {
      setError('The invoice PDF could not be opened. Please try again or contact FOIS.');
    } finally {
      setDownloading(false);
    }
  }

  if (pageState === 'unavailable') {
    return (
      <PageShell>
        <section className="card mx-auto max-w-xl text-center">
          <h1 className="heading-xl text-crimson">Invoice unavailable</h1>
          <p className="mt-3 text-charcoal">{UNAVAILABLE_COPY}</p>
          <Link className="btn-primary mt-6" to="/">
            Return home
          </Link>
        </section>
      </PageShell>
    );
  }

  if (pageState === 'loading' || !invoice) {
    return (
      <PageShell>
        <section className="card mx-auto max-w-xl text-center">
          <p className="text-charcoal">Loading your invoice…</p>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="card mx-auto max-w-2xl">
        <p className="font-display text-xs font-normal uppercase tracking-brand text-kraft">
          {invoice.businessName}
        </p>
        <h1 className="heading-xl mt-3 text-crimson">Invoice {invoice.invoiceNumber}</h1>
        <p className="mt-2 text-sm text-charcoal/80">
          Prepared for {invoice.customerName}. Payment status: {invoice.status}.
        </p>
        <dl className="mt-6 grid gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-charcoal/70">Issue date</dt>
            <dd className="font-semibold">{invoice.issueDate ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-charcoal/70">Due date</dt>
            <dd className="font-semibold">{invoice.dueDate ?? invoice.terms ?? 'Due on receipt'}</dd>
          </div>
          <div>
            <dt className="text-charcoal/70">Total</dt>
            <dd className="font-semibold">{formatMinorUsd(invoice.totalMinor)}</dd>
          </div>
          <div>
            <dt className="text-charcoal/70">Balance due</dt>
            <dd className="font-semibold">{formatMinorUsd(invoice.balanceDueMinor)}</dd>
          </div>
        </dl>
        <table className="mt-6 min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-linen">
              <th className="py-2 pr-4">Description</th>
              <th className="py-2 pr-4">Qty</th>
              <th className="py-2">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems.map((line) => (
              <tr key={`${line.description}-${line.lineNetMinor}`} className="border-b border-linen">
                <td className="py-3 pr-4">{line.description}</td>
                <td className="py-3 pr-4">{line.quantity}</td>
                <td className="py-3">{formatMinorUsd(line.lineNetMinor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {error ? (
          <p className="mt-4 text-sm font-semibold text-crimson" role="alert">
            {error}
          </p>
        ) : null}
        <div className="mt-6 flex flex-wrap gap-3">
          <button className="btn-primary" type="button" disabled={downloading} onClick={() => void onDownload()}>
            {downloading ? 'Opening…' : 'Download PDF'}
          </button>
          <a className="btn-secondary" href={`https://${invoice.website.replace(/^https?:\/\//, '')}`}>
            Visit store
          </a>
        </div>
      </section>
    </PageShell>
  );
}
